import {
  CENTRAL_AUTH_PAGE_PATHS,
  CONTROL_TOWER_SIGN_IN_CLIENT,
  PARTNER_SIGN_IN_CLIENT,
  TERMS_VERSION,
} from '@instanexus/contracts';
import type { ApiClient } from '@instanexus/sdk';
import { setAccessToken } from '@instanexus/web-auth';

import { markPortalAsNew } from '@/lib/portal-cookie';
import { clientAppOrigin, type SignInRequest } from '@/lib/sign-in-request';

/** True when the server knows the accepted version and it is not TERMS_VERSION (null = never accepted). Mirrors AppShell. */
export function isTermsAcceptanceRequired(acceptedVersion: string | null | undefined): boolean {
  return acceptedVersion !== undefined && acceptedVersion !== TERMS_VERSION;
}

/** Leave the central app: a handoff code back to the app when requested, else the app's origin + returnTo. */
export async function leaveCentralSignIn(
  api: Pick<ApiClient, 'auth' | 'partnerAuth'>,
  request: SignInRequest,
  redirectTo: (url: string) => void,
): Promise<void> {
  if (request.handoff !== null) {
    const { client } = request;
    const { codeChallenge, state } = request.handoff;
    const { returnTo } = request;
    const { redirectUrl } =
      client === PARTNER_SIGN_IN_CLIENT
        ? await api.partnerAuth.issueHandoff({ client, codeChallenge, state, returnTo })
        : await api.auth.issueHandoff({ client, codeChallenge, state, returnTo });
    redirectTo(redirectUrl);
    return;
  }
  const origin = clientAppOrigin(request.client);
  if (origin === null) throw new Error('No origin is configured for this app.');
  redirectTo(new URL(request.returnTo, origin).toString());
}

/**
 * The end of every central sign-in (password, provider callback): a partner leaves directly (its portal
 * asks its own Terms); anyone else gets the token stored, `me` read, the Terms interstitial when owed
 * (`${CENTRAL_AUTH_PAGE_PATHS.terms}?${search}`), else `leaveCentralSignIn`.
 */
export async function finishCentralSignIn(
  api: Pick<ApiClient, 'auth' | 'partnerAuth'>,
  request: SignInRequest,
  search: string,
  accessToken: string,
  navigation: {
    readonly navigate: (to: string) => void;
    readonly redirectTo: (url: string) => void;
  },
): Promise<void> {
  if (request.client === PARTNER_SIGN_IN_CLIENT) {
    await leaveCentralSignIn(api, request, navigation.redirectTo);
    return;
  }
  // Customers only: partners returned above, and an operator's browser is not a customer's.
  if (request.client !== CONTROL_TOWER_SIGN_IN_CLIENT) markPortalAsNew();
  setAccessToken(accessToken);
  const me = await api.auth.me();
  if (isTermsAcceptanceRequired(me.termsVersion)) {
    navigation.navigate(`${CENTRAL_AUTH_PAGE_PATHS.terms}?${search}`);
    return;
  }
  await leaveCentralSignIn(api, request, navigation.redirectTo);
}
